/*
  CODEX CUSTOMIZATION
  File: custom-pick-six.js
  Scope: product template suffix 'pick-six' only
  Purpose:
  - Replace EB bundle slot labels from "Product N" to "Mini Bottle N".
  - Remove "100ML Sample" from dynamic bundle UI text.
  - Handle app-rendered modal/portal content that can mount outside the block wrapper.
  - Make the app's div-based bundle controls and product-selector dialog keyboard accessible.
  - Hide the app's decorative empty-slot plus icons from assistive technology.
*/

(() => {
  const PRODUCT_SLOT_PATTERN = /\bProduct(?=\s*\d+\b)/g;
  const SAMPLE_SUFFIX_TEST_PATTERN = /100ML Sample/i;
  const SAMPLE_SUFFIX_PATTERN = /\s*100ML Sample\b/gi;
  const QUICK_TEXT_TEST_PATTERN = /Product\s*\d+|100ML Sample/i;
  const REPLACEMENT = 'Mini Bottle';
  const BUNDLE_ROOT_SELECTOR = '.gbbMixAndMatchBundleWrapper';
  const BUNDLE_MODAL_SELECTOR = '.gbbMixProductsModal';
  const PRODUCT_ITEM_SELECTOR = '.gbbMixProductItem';
  const SELECTED_PRODUCT_CARD_SELECTOR = '.gbbMixSelectedProductCard';
  const EMPTY_STATE_ICON_SELECTOR = '.gbbMixEmptyStateCardIconWrapper svg';
  const ENHANCED_CONTROL_ATTRIBUTE = 'data-codex-pick-six-control';
  const ENHANCED_CONTROL_SELECTOR = `[${ENHANCED_CONTROL_ATTRIBUTE}]`;
  const MODAL_FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    `[${ENHANCED_CONTROL_ATTRIBUTE}]:not([tabindex="-1"])`,
    '[tabindex]:not([tabindex="-1"])',
  ].join(', ');

  let accessibilitySyncQueued = false;
  let bundleModalWasOpen = false;
  let modalReturnFocusElement = null;
  let pendingBundleFocus = null;

  function replaceProductSlotLabelInTextNode(node) {
    if (!(node instanceof Text)) {
      return;
    }

    const currentValue = node.nodeValue;

    if (!currentValue) {
      return;
    }

    if (currentValue.indexOf('Product') === -1 && !SAMPLE_SUFFIX_TEST_PATTERN.test(currentValue)) {
      return;
    }

    const nextValue = currentValue
      .replace(PRODUCT_SLOT_PATTERN, REPLACEMENT)
      .replace(SAMPLE_SUFFIX_PATTERN, '');

    if (nextValue !== currentValue) {
      node.nodeValue = nextValue;
    }
  }

  function replaceProductSlotLabelsWithin(root) {
    if (!(root instanceof Element || root instanceof DocumentFragment)) {
      return;
    }

    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let textNode = walker.nextNode();

    while (textNode) {
      replaceProductSlotLabelInTextNode(textNode);
      textNode = walker.nextNode();
    }
  }

  function processCandidateNode(node) {
    if (node instanceof Text) {
      replaceProductSlotLabelInTextNode(node);
      return;
    }

    if (!(node instanceof Element || node instanceof DocumentFragment)) {
      return;
    }

    const snapshotText = node.textContent;

    if (!snapshotText || !QUICK_TEXT_TEST_PATTERN.test(snapshotText)) {
      return;
    }

    replaceProductSlotLabelsWithin(node);
  }

  function normalizeText(value) {
    return String(value || '').replace(/\s+/g, ' ').trim();
  }

  function isNativeInteractiveControl(element) {
    return element.matches('button, input, select, textarea, a[href]');
  }

  function isAppControlDisabled(element) {
    return element.hasAttribute('disabled')
      || Array.from(element.classList).some((className) => /disabled/i.test(className));
  }

  function setAccessibleButton(element, label, options = {}) {
    if (!(element instanceof HTMLElement)) {
      return;
    }

    const disabled = options.disabled ?? isAppControlDisabled(element);

    if (!isNativeInteractiveControl(element)) {
      element.setAttribute('role', 'button');
      element.setAttribute(ENHANCED_CONTROL_ATTRIBUTE, 'true');
    }

    element.setAttribute('tabindex', disabled ? '-1' : '0');
    element.setAttribute('aria-disabled', disabled ? 'true' : 'false');

    if (label) {
      element.setAttribute('aria-label', label);
    }

    if (typeof options.pressed === 'boolean') {
      element.setAttribute('aria-pressed', options.pressed ? 'true' : 'false');
    }
  }

  function focusElement(element) {
    if (!(element instanceof HTMLElement)) {
      return false;
    }

    try {
      element.focus({ preventScroll: true });
    } catch (error) {
      element.focus();
    }

    return document.activeElement === element;
  }

  function getProductTitle(element) {
    const productItem = element.closest('.gbbMixProductItem, .gbbMixSelectedProductCard');
    return normalizeText(productItem?.querySelector('.gbbMixProductTitle')?.textContent) || 'olive oil sample';
  }

  function getEmptySlotLabel(element) {
    return normalizeText(element.querySelector('.gbbMixEmptyStateCardText')?.textContent || element.textContent);
  }

  function rememberPendingBundleFocus(control) {
    if (control.matches('.gbbMixProductAddButton')) {
      const productItem = control.closest('.gbbMixProductItem, .gbbMixSelectedProductCard');

      if (!(productItem instanceof HTMLElement)) {
        return;
      }

      pendingBundleFocus = {
        type: 'added-product',
        productId: productItem.dataset.productId || '',
        variantId: productItem.dataset.variantId || '',
        productTitle: getProductTitle(control),
        expiresAt: Date.now() + 2000,
      };
      return;
    }

    if (control.matches('.gbbMixProductsQuantityDecrementButton')) {
      const productItem = control.closest('.gbbMixProductItem, .gbbMixSelectedProductCard');

      if (!(productItem instanceof HTMLElement)) {
        return;
      }

      pendingBundleFocus = {
        type: 'removed-product',
        productId: productItem.dataset.productId || '',
        variantId: productItem.dataset.variantId || '',
        productTitle: getProductTitle(control),
        emptySlotLabels: new Set(Array.from(document.querySelectorAll('.gbbMixEmptyStateCard'), getEmptySlotLabel)),
        expiresAt: Date.now() + 2000,
      };
      return;
    }

    if (control.matches('.gbbMixProductPageCategorySelectedPrtoductIconWrapper')) {
      pendingBundleFocus = {
        type: 'removed-product',
        productId: control.closest('.gbbMixProductItem, .gbbMixSelectedProductCard')?.dataset.productId || '',
        variantId: control.closest('.gbbMixProductItem, .gbbMixSelectedProductCard')?.dataset.variantId || '',
        productTitle: getProductTitle(control),
        emptySlotLabels: new Set(Array.from(document.querySelectorAll('.gbbMixEmptyStateCard'), getEmptySlotLabel)),
        expiresAt: Date.now() + 2000,
      };
    }
  }

  function findAddedProductFocusTarget(pendingFocus) {
    const searchRoots = [document.querySelector(BUNDLE_MODAL_SELECTOR), document.querySelector(BUNDLE_ROOT_SELECTOR), document].filter((root) => root instanceof Document || root instanceof Element);

    for (const root of searchRoots) {
      const matchingItem = Array.from(root.querySelectorAll(`${PRODUCT_ITEM_SELECTOR}, ${SELECTED_PRODUCT_CARD_SELECTOR}`)).find((element) => {
        const productIdMatches = !pendingFocus.productId || element.dataset.productId === pendingFocus.productId;
        const variantIdMatches = !pendingFocus.variantId || element.dataset.variantId === pendingFocus.variantId;
        const titleMatches = getProductTitle(element) === pendingFocus.productTitle;
        const hasProductIdentifier = pendingFocus.productId || pendingFocus.variantId;

        return productIdMatches && variantIdMatches && (hasProductIdentifier || titleMatches);
      });

      if (!(matchingItem instanceof HTMLElement)) {
        continue;
      }

      const focusTarget = matchingItem.querySelector('.gbbMixProductsQuantityDecrementButton:not([aria-disabled="true"]), .gbbMixProductsQuantityIncrementButton:not([aria-disabled="true"]), .gbbMixProductPageCategorySelectedPrtoductIconWrapper:not([aria-disabled="true"])');

      return focusTarget instanceof HTMLElement ? focusTarget : matchingItem;
    }

    return null;
  }

  function findRemovedProductFocusTarget(pendingFocus) {
    const searchRoots = [document.querySelector(BUNDLE_MODAL_SELECTOR), document.querySelector(BUNDLE_ROOT_SELECTOR), document].filter((root) => root instanceof Document || root instanceof Element);

    for (const root of searchRoots) {
      const matchingItem = Array.from(root.querySelectorAll(`${PRODUCT_ITEM_SELECTOR}, ${SELECTED_PRODUCT_CARD_SELECTOR}`)).find((element) => {
        const productIdMatches = !pendingFocus.productId || element.dataset.productId === pendingFocus.productId;
        const variantIdMatches = !pendingFocus.variantId || element.dataset.variantId === pendingFocus.variantId;
        const titleMatches = getProductTitle(element) === pendingFocus.productTitle;
        const hasProductIdentifier = pendingFocus.productId || pendingFocus.variantId;

        return productIdMatches && variantIdMatches && (hasProductIdentifier || titleMatches);
      });

      if (!(matchingItem instanceof HTMLElement)) {
        continue;
      }

      const focusTarget = matchingItem.querySelector('.gbbMixProductAddButton:not([aria-disabled="true"]), .gbbMixProductsQuantityDecrementButton:not([aria-disabled="true"]), .gbbMixProductsQuantityIncrementButton:not([aria-disabled="true"]), .gbbMixProductPageCategorySelectedPrtoductIconWrapper:not([aria-disabled="true"])');

      return focusTarget instanceof HTMLElement ? focusTarget : matchingItem;
    }

    const emptySlots = Array.from(document.querySelectorAll('.gbbMixEmptyStateCard'));
    const newEmptySlot = emptySlots.find((element) => {
      const slotLabel = getEmptySlotLabel(element);

      return slotLabel
        && !pendingFocus.emptySlotLabels.has(slotLabel)
        && element.getAttribute('aria-disabled') !== 'true';
    });

    return newEmptySlot || null;
  }

  function restorePendingBundleFocus() {
    if (!pendingBundleFocus) {
      return;
    }

    if (Date.now() > pendingBundleFocus.expiresAt) {
      pendingBundleFocus = null;
      return;
    }

    const focusTarget = pendingBundleFocus.type === 'added-product'
      ? findAddedProductFocusTarget(pendingBundleFocus)
      : findRemovedProductFocusTarget(pendingBundleFocus);

    if (!(focusTarget instanceof HTMLElement) || !isElementVisible(focusTarget)) {
      return;
    }

    pendingBundleFocus = null;
    focusTarget.focus({ preventScroll: true });
  }

  function enhanceBundleControls() {
    document.querySelectorAll(EMPTY_STATE_ICON_SELECTOR).forEach((icon) => {
      icon.setAttribute('aria-hidden', 'true');
      icon.setAttribute('focusable', 'false');
    });

    document.querySelectorAll('.gbbMixEmptyStateCard').forEach((element) => {
      const slotLabel = normalizeText(element.querySelector('.gbbMixEmptyStateCardText')?.textContent || element.textContent);
      setAccessibleButton(element, `Choose ${slotLabel || 'an olive oil sample'}`);
    });

    document.querySelectorAll(`${PRODUCT_ITEM_SELECTOR}, ${SELECTED_PRODUCT_CARD_SELECTOR}`).forEach((element) => {
      element.setAttribute('tabindex', '-1');
    });

    document.querySelectorAll('.gbbMixModalCloseIconWrapper, .gbbMixModalCloseIconMobileIconWrapper').forEach((element) => {
      setAccessibleButton(element, 'Close product selector');
    });

    document.querySelectorAll('.gbbMixCategory').forEach((element) => {
      const category = normalizeText(element.textContent) || 'category';
      setAccessibleButton(element, `Filter products: ${category}`, {
        pressed: element.classList.contains('gbbMixCategorySelected'),
      });
    });

    document.querySelectorAll('.gbbMixLeftSliderBtn').forEach((element) => {
      setAccessibleButton(element, 'Scroll categories left');
    });

    document.querySelectorAll('.gbbMixRightSliderBtn').forEach((element) => {
      setAccessibleButton(element, 'Scroll categories right');
    });

    document.querySelectorAll('.gbbMixProductAddButton').forEach((element) => {
      setAccessibleButton(element, `Add ${getProductTitle(element)}`);
    });

    document.querySelectorAll('.gbbMixProductsQuantityDecrementButton').forEach((element) => {
      setAccessibleButton(element, `Decrease ${getProductTitle(element)} quantity`);
    });

    document.querySelectorAll('.gbbMixProductsQuantityIncrementButton').forEach((element) => {
      setAccessibleButton(element, `Increase ${getProductTitle(element)} quantity`);
    });

    document.querySelectorAll('.gbbMixFooterNextButton').forEach((element) => {
      setAccessibleButton(element, 'Done selecting products');
    });

    document.querySelectorAll('.gbbMixAddtoCartBtn').forEach((element) => {
      setAccessibleButton(element, 'Add sampler to cart');
    });

    document.querySelectorAll('.gbbMixProductPageCategorySelectedPrtoductIconWrapper').forEach((element) => {
      setAccessibleButton(element, `Remove ${getProductTitle(element)} from sampler`);
    });
  }

  function isElementVisible(element) {
    return element instanceof HTMLElement
      && !element.hidden
      && element.getAttribute('aria-hidden') !== 'true'
      && element.getClientRects().length > 0;
  }

  function getModalFocusableElements(modal) {
    return Array.from(modal.querySelectorAll(MODAL_FOCUSABLE_SELECTOR)).filter((element) => {
      return isElementVisible(element) && element.getAttribute('aria-disabled') !== 'true';
    });
  }

  function focusModal(modal) {
    const closeControl = Array.from(modal.querySelectorAll('.gbbMixModalCloseIconWrapper, .gbbMixModalCloseIconMobileIconWrapper')).find(isElementVisible);
    const focusTarget = closeControl || getModalFocusableElements(modal)[0] || modal;

    window.requestAnimationFrame(() => {
      focusElement(focusTarget);
    });
  }

  function syncBundleModalAccessibility() {
    const modal = document.querySelector(BUNDLE_MODAL_SELECTOR);
    const modalIsOpen = modal instanceof HTMLElement && modal.classList.contains('gbbMixProductsModalOpen');

    if (!(modal instanceof HTMLElement)) {
      bundleModalWasOpen = false;
      return;
    }

    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-label', 'Choose olive oil samples');
    modal.setAttribute('tabindex', '-1');
    modal.setAttribute('aria-hidden', modalIsOpen ? 'false' : 'true');

    if (modalIsOpen) {
      modal.setAttribute('aria-modal', 'true');
    } else {
      modal.removeAttribute('aria-modal');
      modal.querySelectorAll(ENHANCED_CONTROL_SELECTOR).forEach((element) => {
        element.setAttribute('tabindex', '-1');
      });
    }

    if (modalIsOpen && !bundleModalWasOpen) {
      focusModal(modal);
    } else if (!modalIsOpen && bundleModalWasOpen) {
      const returnFocusElement = modalReturnFocusElement?.isConnected
        ? modalReturnFocusElement
        : document.querySelector('.gbbMixEmptyStateCard:not([aria-disabled="true"]), .gbbMixProductPageCategorySelectedPrtoductIconWrapper:not([aria-disabled="true"]), .gbbMixAddtoCartBtn:not([aria-disabled="true"])');

      if (returnFocusElement instanceof HTMLElement) {
        window.requestAnimationFrame(() => {
          focusElement(returnFocusElement);
        });
      }
      modalReturnFocusElement = null;
    }

    bundleModalWasOpen = modalIsOpen;
  }

  function syncBundleAccessibility() {
    accessibilitySyncQueued = false;

    if (!document.querySelector(`${BUNDLE_ROOT_SELECTOR}, ${BUNDLE_MODAL_SELECTOR}`)) {
      return;
    }

    enhanceBundleControls();
    syncBundleModalAccessibility();
    restorePendingBundleFocus();
  }

  function queueBundleAccessibilitySync() {
    if (accessibilitySyncQueued) {
      return;
    }

    accessibilitySyncQueued = true;
    window.requestAnimationFrame(syncBundleAccessibility);
  }

  function handleBundleControlClick(event) {
    const control = event.target instanceof Element
      ? event.target.closest(`${ENHANCED_CONTROL_SELECTOR}, .gbbMixEmptyStateCard, .gbbMixProductAddButton, .gbbMixProductPageCategorySelectedPrtoductIconWrapper`)
      : null;

    if (!(control instanceof HTMLElement)) {
      return;
    }

    if (control.matches('.gbbMixAddtoCartBtn')) {
      // The bundle app submits its own cart request from a non-native control,
      // so the theme's product-form start event is never emitted and the
      // drawer cannot remember the invoking control. Keep this bridge scoped
      // to the app's final Add-to-cart control.
      document.dispatchEvent(new CustomEvent('cart:add:start', {
        bubbles: true,
        detail: {
          submitter: control,
          form: null,
          externalCartAdd: true,
        },
      }));
      return;
    }

    if (control.matches('.gbbMixEmptyStateCard')) {
      modalReturnFocusElement = control;
    }

    if (event.detail === 0) {
      rememberPendingBundleFocus(control);
    }
  }

  function handleBundleKeyboard(event) {
    const modal = document.querySelector(BUNDLE_MODAL_SELECTOR);
    const modalIsOpen = modal instanceof HTMLElement && modal.classList.contains('gbbMixProductsModalOpen');

    if (event.key === 'Escape' && modalIsOpen && modal.contains(event.target)) {
      const closeControl = Array.from(modal.querySelectorAll('.gbbMixModalCloseIconWrapper, .gbbMixModalCloseIconMobileIconWrapper')).find(isElementVisible);

      if (closeControl instanceof HTMLElement) {
        event.preventDefault();
        closeControl.click();
      }
      return;
    }

    if (event.key === 'Tab' && modalIsOpen) {
      const focusableElements = getModalFocusableElements(modal);

      if (focusableElements.length === 0) {
        event.preventDefault();
        modal.focus();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (!modal.contains(document.activeElement)) {
        event.preventDefault();
        (event.shiftKey ? lastElement : firstElement).focus();
      } else if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
      return;
    }

    if (!['Enter', ' '].includes(event.key) || event.repeat) {
      return;
    }

    const control = event.target instanceof Element ? event.target.closest(ENHANCED_CONTROL_SELECTOR) : null;

    if (!(control instanceof HTMLElement) || control.getAttribute('aria-disabled') === 'true') {
      return;
    }

    event.preventDefault();
    control.click();
  }

  function initializePickSixEnhancements() {
    if (!document.body) {
      return;
    }

    processCandidateNode(document.body);
    queueBundleAccessibilitySync();

    const observer = new MutationObserver((mutationList) => {
      mutationList.forEach((mutation) => {
        if (mutation.type === 'characterData') {
          processCandidateNode(mutation.target);
          queueBundleAccessibilitySync();
          return;
        }

        if (mutation.type === 'attributes') {
          queueBundleAccessibilitySync();
          return;
        }

        mutation.addedNodes.forEach(processCandidateNode);

        if (mutation.addedNodes.length > 0) {
          queueBundleAccessibilitySync();
        }
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['class'],
    });

    document.addEventListener('click', handleBundleControlClick, true);
    document.addEventListener('keydown', handleBundleKeyboard, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializePickSixEnhancements, { once: true });
  } else {
    initializePickSixEnhancements();
  }
})();
